import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useGetProjectsQuery } from 'features/projects';
import { RotateCcw } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  useCreateDemoChatClientMutation,
  useCreateDemoChatMessageMutation,
  useGetDemoChatMessagesQuery,
} from '../api/demoChat.api';
import { clearDemoChatClientId, readDemoChatClientId, writeDemoChatClientId } from '../lib/clientStorage';
import usePusher from '../lib/usePusher';
import type { DemoChatMessage } from '../model/message';
import MessageComposer from './MessageComposer';
import MessageList from './MessageList';

interface PusherNewMessagePayload {
  message: DemoChatMessage;
}

const DemoChatView = () => {
  const { masterProjectId } = useParams<{ masterProjectId: string }>();
  const id = Number(masterProjectId);

  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery();

  const pusherIdentification = useMemo(() => {
    if (!id) return null;
    const current = projects.find((project) => project.id === id);
    if (!current) return null;
    if (current.masterProjectId === null) {
      return current.pusherIdentification ?? null;
    }
    const master = projects.find((project) => project.id === current.masterProjectId);
    return master?.pusherIdentification ?? null;
  }, [id, projects]);

  const [clientPhoneIdentifier, setClientPhoneIdentifier] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);

  useEffect(() => {
    if (!pusherIdentification) {
      setClientPhoneIdentifier(null);
      return;
    }
    setClientPhoneIdentifier(readDemoChatClientId(pusherIdentification));
  }, [pusherIdentification]);

  const [createDemoChatClient, { isLoading: isCreatingClient }] = useCreateDemoChatClientMutation();
  const [createDemoChatMessage] = useCreateDemoChatMessageMutation();

  const {
    data: messages,
    refetch: refetchMessages,
    isFetching: isFetchingMessages,
  } = useGetDemoChatMessagesQuery(
    { clientPhoneIdentifier: clientPhoneIdentifier ?? '' },
    { skip: !clientPhoneIdentifier }
  );

  usePusher<PusherNewMessagePayload>({
    channelName: clientPhoneIdentifier ? `chat-${clientPhoneIdentifier}` : null,
    eventName: 'new-message',
    enabled: Boolean(clientPhoneIdentifier),
    onEvent: () => {
      if (justSent) {
        setJustSent(false);
        return;
      }
      refetchMessages().catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error refetching demo chat messages');
      });
    },
  });

  const handleStartChat = useCallback(async () => {
    if (!pusherIdentification) return;
    try {
      const result = await createDemoChatClient({ companyIdentifier: pusherIdentification }).unwrap();
      writeDemoChatClientId(pusherIdentification, result.id);
      setClientPhoneIdentifier(result.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating demo chat client', err);
    }
  }, [createDemoChatClient, pusherIdentification]);

  const handleResetChat = useCallback(() => {
    if (!pusherIdentification) return;
    clearDemoChatClientId(pusherIdentification);
    setClientPhoneIdentifier(null);
  }, [pusherIdentification]);

  const handleSendMessage = useCallback(
    async (text: string, files: File[]) => {
      if (!clientPhoneIdentifier) return;
      if (!text && files.length === 0) return;
      setJustSent(true);
      try {
        if (files.length === 0) {
          await createDemoChatMessage({ clientPhoneIdentifier, message: text }).unwrap();
        } else {
          await Promise.all(
            files.map((file, idx) =>
              createDemoChatMessage({ clientPhoneIdentifier, message: idx === 0 ? text : '', file }).unwrap()
            )
          );
        }
        await refetchMessages();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error sending demo chat message', err);
      }
    },
    [clientPhoneIdentifier, createDemoChatMessage, refetchMessages]
  );

  if (isLoadingProjects) {
    return (
      <section className="flex min-h-0 flex-1 flex-col gap-6">
        <Skeleton className="h-8 w-64 shrink-0 rounded-lg" />
        <Skeleton className="min-h-0 flex-1 rounded-xl" />
      </section>
    );
  }

  if (!pusherIdentification) {
    return (
      <section className="flex min-h-0 flex-1 flex-col gap-6">
        <header>
          <p className="mb-1 font-body text-xs font-bold uppercase tracking-widest text-text-subtle">Proyecto</p>
          <h2 className="m-0 font-display text-2xl font-bold text-text-default">Demo Chat</h2>
        </header>
        <Alert className="rounded-xl border-border-subtle bg-bg-surface">
          <AlertTitle>Demo Chat no disponible</AlertTitle>
          <AlertDescription className="text-text-muted">
            Este proyecto no tiene un identificador de Pusher (companyIdentifier) configurado. Configúralo en el
            proyecto master para poder usar el Demo Chat.
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-6">
      <header className="flex shrink-0 items-start justify-between gap-4">
        <div>
          <p className="mb-1 font-body text-xs font-bold uppercase tracking-widest text-text-subtle">Proyecto</p>
          <h2 className="m-0 font-display text-2xl font-bold text-text-default">Demo Chat</h2>
          <p className="mt-1 text-xs text-text-muted">
            Usa la versión Latest de cada prompt con su modelo IA. Si no hay Latest, usa la de Production.
          </p>
        </div>
        {clientPhoneIdentifier && (
          <Button variant="outline" size="sm" type="button" onClick={handleResetChat}>
            <RotateCcw className="size-4" />
            Reiniciar chat
          </Button>
        )}
      </header>

      {!clientPhoneIdentifier ? (
        <div className="flex flex-col items-center justify-center gap-6 rounded-xl border border-border-subtle bg-bg-surface px-6 py-8">
          <p className="max-w-md text-center font-body text-sm text-text-muted">
            Inicia un nuevo chat de prueba para conversar con el agente como si fueras un cliente.
          </p>
          <Button
            type="button"
            onClick={() => {
              void handleStartChat();
            }}
            disabled={isCreatingClient}
          >
            {isCreatingClient ? 'Iniciando…' : 'Iniciar Demo Chat'}
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            'funnel-chat-panel flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border-subtle bg-bg-surface shadow-[var(--shadow-sm)]'
          )}
        >
          <MessageList messages={messages ?? []} isLoading={isFetchingMessages} />
          <MessageComposer
            onSend={(text, files) => {
              void handleSendMessage(text, files);
            }}
          />
        </div>
      )}
    </section>
  );
};

export default DemoChatView;
